import { Rise } from "@/components/humble/AnimatedText";
import ChapterMark from "@/components/humble/ChapterMark";
import Painting from "@/components/humble/Painting";
import { HUMBLE } from "@/constants/testIds";

/**
 * Joy & Grief — two-panel editorial spread on parchment.
 * Fully readable; no dark bleed from adjacent section here.
 */
export default function JoyAndGrief() {
  return (
    <section
      id="joy-grief"
      data-testid={HUMBLE.joySection}
      className="relative isolate overflow-hidden bg-parchment text-earth canvas-tex"
    >
      <ChapterMark numeral="II" label="Joy & grief" align="right" />

      <div className="relative z-[1] mx-auto w-full max-w-[1400px] px-6 py-28 md:px-12 md:py-40">
        <Rise>
          <div className="mb-4 flex items-center gap-3 font-body uppercase text-[11px] tracking-[0.42em] text-earth/60">
            <span className="inline-block h-px w-8 bg-earth/40" />
            Chapter II
          </div>
        </Rise>
        <Rise delay={0.05}>
          <h2 className="max-w-3xl font-display text-4xl leading-[1.05] tracking-tight text-earth md:text-5xl lg:text-[60px]">
            Homesteading is joy and grief in the same week.
          </h2>
        </Rise>
        <Rise delay={0.15}>
          <p className="mt-6 max-w-2xl font-body text-lg text-earth/80">
            The wonder of candling and hatching eggs, and the ache of losing
            chicks after a storm. Humble Roots is built to hold both — quietly,
            without judgment, so nothing important slips through the cracks.
          </p>
        </Rise>

        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          {/* Joy — glowing egg */}
          <Rise className="lg:col-span-7">
            <figure className="group relative">
              <Painting
                src="/art/candling-egg.png"
                alt="A painted close-up of two hands cupped around a warmly glowing egg being candled."
                wrapperClassName="aspect-[5/4]"
              />
              <figcaption className="mt-5 flex items-start gap-4">
                <span className="mt-1 inline-block h-px w-10 bg-earth/40" />
                <div>
                  <div className="font-body uppercase text-[11px] tracking-[0.4em] text-earth/60">
                    Joy · candling night
                  </div>
                  <div className="mt-1 font-display italic text-xl text-earth md:text-2xl">
                    &ldquo;There&rsquo;s something moving in there.&rdquo;
                  </div>
                </div>
              </figcaption>
            </figure>
          </Rise>

          {/* Grief — stormy barn */}
          <Rise className="lg:col-span-5 lg:mt-24" delay={0.1}>
            <figure className="group relative">
              <Painting
                src="/art/stormy-barn.png"
                alt="A painted dark wooden coop in a rainstorm with a single warm lantern glowing in the window."
                wrapperClassName="aspect-[3/4]"
              />
              <figcaption className="mt-5 flex items-start gap-4">
                <span className="mt-1 inline-block h-px w-10 bg-earth/40" />
                <div>
                  <div className="font-body uppercase text-[11px] tracking-[0.4em] text-earth/60">
                    Grief · after the storm
                  </div>
                  <div className="mt-1 font-display italic text-xl text-earth md:text-2xl">
                    &ldquo;We lost three. We still went to work Monday.&rdquo;
                  </div>
                </div>
              </figcaption>
            </figure>
          </Rise>
        </div>
      </div>
    </section>
  );
}
